import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { IComponent, rage, useObservable } from '~/rage';
import { ClothesComponentId } from '~/shared';
import { ButtonActions } from '../../components/ButtonActions';
import { StyledContainer } from '../../components/Container';
import { DropdownBlock } from '../../components/DropdownBlock';
import { SelectorBlock } from '../../components/SelectorBlock';
import { ToggleBlock } from '../../components/ToggleBlock';
import {
	CLOTH_SECTIONS,
	INITIAL_CLOTH_SECTION,
	INITIAL_PROP_SECTION,
} from './ClothTester.model';

interface IComponentRange {
	drawable: [number, number];
	texture: [number, number];
}

const INITIAL_CLOTHES_COMPONENTS_RANGES: Record<
	ClothesComponentId,
	IComponentRange
> = {
	[ClothesComponentId.HEAD]: { drawable: [0, 0], texture: [0, 0] },
	[ClothesComponentId.MASK]: { drawable: [0, 0], texture: [0, 0] },
	[ClothesComponentId.HAIR]: { drawable: [0, 0], texture: [0, 0] },
	[ClothesComponentId.TORSO]: { drawable: [0, 0], texture: [0, 0] },
	[ClothesComponentId.LEGS]: { drawable: [0, 0], texture: [0, 0] },
	[ClothesComponentId.BACKPACK]: { drawable: [0, 0], texture: [0, 0] },
	[ClothesComponentId.SHOES]: { drawable: [0, 0], texture: [0, 0] },
	[ClothesComponentId.ACCESSORIES]: { drawable: [0, 0], texture: [0, 0] },
	[ClothesComponentId.UNDERSHITS]: { drawable: [0, 0], texture: [-1, 0] },
	[ClothesComponentId.ARMORS]: { drawable: [0, 0], texture: [0, 0] },
	[ClothesComponentId.DECALS]: { drawable: [0, 0], texture: [0, 0] },
	[ClothesComponentId.TOPS]: { drawable: [0, 0], texture: [0, 0] },
	[ClothesComponentId.HATS]: { drawable: [0, 0], texture: [-1, 0] },
	[ClothesComponentId.GLASSES]: { drawable: [0, 0], texture: [-1, 0] },
	[ClothesComponentId.EARS]: { drawable: [0, 0], texture: [-1, 0] },
	[ClothesComponentId.WATCHES]: { drawable: [0, 0], texture: [-1, 0] },
	[ClothesComponentId.BRACELETS]: { drawable: [0, 0], texture: [-1, 0] },
};

const INITIAL_CLOTHES_COMPONENTS: Record<ClothesComponentId, IComponent> = {
	[ClothesComponentId.HEAD]: { drawable: 0, texture: 0 },
	[ClothesComponentId.MASK]: { drawable: 0, texture: 0 },
	[ClothesComponentId.HAIR]: { drawable: 0, texture: 0 },
	[ClothesComponentId.TORSO]: { drawable: 0, texture: -1 },
	[ClothesComponentId.LEGS]: { drawable: 0, texture: 0 },
	[ClothesComponentId.BACKPACK]: { drawable: 0, texture: 0 },
	[ClothesComponentId.SHOES]: { drawable: 0, texture: 0 },
	[ClothesComponentId.ACCESSORIES]: { drawable: 0, texture: 0 },
	[ClothesComponentId.UNDERSHITS]: { drawable: 0, texture: 0 },
	[ClothesComponentId.ARMORS]: { drawable: 0, texture: 0 },
	[ClothesComponentId.DECALS]: { drawable: 0, texture: 0 },
	[ClothesComponentId.TOPS]: { drawable: 0, texture: 0 },
	[ClothesComponentId.HATS]: { drawable: 0, texture: -1 },
	[ClothesComponentId.GLASSES]: { drawable: 0, texture: -1 },
	[ClothesComponentId.EARS]: { drawable: 0, texture: -1 },
	[ClothesComponentId.WATCHES]: { drawable: 0, texture: -1 },
	[ClothesComponentId.BRACELETS]: { drawable: 0, texture: -1 },
};

async function getComponentRange(
	componentId: ClothesComponentId,
	drawableId: number,
): Promise<IComponentRange> {
	const drawableCount = await rage.clothesTester.getComponentDrawablesCount(
		componentId,
	);
	const textureCount = await rage.clothesTester.getComponentTexturesCount(
		componentId,
		drawableId,
	);
	return {
		drawable: [
			INITIAL_CLOTHES_COMPONENTS_RANGES[componentId].drawable[0],
			drawableCount - 1,
		],
		texture: [
			INITIAL_CLOTHES_COMPONENTS_RANGES[componentId].texture[0],
			textureCount - 1,
		],
	};
}

const ClothesTester: React.FC = () => {
	const [clothComponentId, setClothComponentId] = useState(
		INITIAL_CLOTH_SECTION.value,
	);
	const [propComponentId, setPropComponentId] = useState(
		INITIAL_PROP_SECTION.value,
	);

	const initialComponents = useObservable(rage.clothesTester.getComponents());
	const [currentComponents, setCurrentComponents] = useState<
		Record<ClothesComponentId, IComponent>
	>(INITIAL_CLOTHES_COMPONENTS);
	const [currentComponentsRanges, setCurrentComponentsRanges] = useState<
		Record<ClothesComponentId, IComponentRange>
	>(INITIAL_CLOTHES_COMPONENTS_RANGES);

	const [isHatHidden, setIsHatHidden] = useState<boolean>(false);
	const [areGlassesHidden, setAreGlassesHidden] = useState<boolean>(false);
	const [isBellyOpen, setIsBellyOpen] = useState<boolean>(false);

	useEffect(() => {
		setCurrentComponents(initialComponents);
		updateInitialComponentRanges().catch((err) =>
			rage.notifications.showException(err),
		);
	}, [initialComponents]);

	useEffect(() => {
		rage.clothesTester
			.applyComponents(
				clothComponentId,
				currentComponents[clothComponentId].drawable,
				currentComponents[clothComponentId].texture,
				currentComponents[ClothesComponentId.TORSO].drawable,
				currentComponents[ClothesComponentId.TORSO].texture,
				currentComponents[ClothesComponentId.UNDERSHITS].drawable,
				currentComponents[ClothesComponentId.UNDERSHITS].texture,
				isHatHidden,
				areGlassesHidden,
				isBellyOpen,
			)
			.catch((err) => rage.notifications.showException(err));

		rage.clothesTester
			.applyComponents(
				propComponentId,
				currentComponents[propComponentId].drawable,
				currentComponents[propComponentId].texture,
				currentComponents[ClothesComponentId.TORSO].drawable,
				currentComponents[ClothesComponentId.TORSO].texture,
				currentComponents[ClothesComponentId.UNDERSHITS].drawable,
				currentComponents[ClothesComponentId.UNDERSHITS].texture,
				isHatHidden,
				areGlassesHidden,
				isBellyOpen,
			)
			.catch((err) => rage.notifications.showException(err));
	}, [
		currentComponents,
		clothComponentId,
		propComponentId,
		isHatHidden,
		areGlassesHidden,
		isBellyOpen,
	]);

	const updateComponentRanges = useCallback(
		async (...componentIds: ClothesComponentId[]) => {
			const componentRanges = await Promise.all(
				componentIds.map((id) =>
					getComponentRange(id, currentComponents[id].drawable),
				),
			);

			const newValues: Partial<Record<ClothesComponentId, IComponentRange>> =
				{};
			for (let i = 0; i < componentIds.length; i++) {
				const componentId = componentIds[i];
				const componentRange = componentRanges[i];
				newValues[componentId] = componentRange;
			}

			setCurrentComponentsRanges((prevValue) => ({
				...prevValue,
				...newValues,
			}));
		},
		[currentComponents],
	);

	const updateInitialComponentRanges = useCallback(async () => {
		await updateComponentRanges(
			clothComponentId,
			propComponentId,
			ClothesComponentId.TORSO,
			ClothesComponentId.UNDERSHITS,
		);
	}, [clothComponentId, propComponentId, currentComponents]);

	useEffect(() => {
		updateComponentRanges(clothComponentId, propComponentId).catch(
			rage.notifications.showException,
		);
	}, [clothComponentId, propComponentId, currentComponents]);

	const handleDropdownChange = (
		newValue: ClothesComponentId,
		options: { isProp: boolean },
	) => {
		if (!options.isProp) {
			setClothComponentId(newValue);
			rage.clothesTester
				.lookAtComponent(newValue)
				.catch((err) => rage.notifications.showException(err));
		} else {
			setPropComponentId(newValue);
		}
	};

	const clothSelectorTitle = useMemo(
		() =>
			CLOTH_SECTIONS.find((e) => e.value === clothComponentId)?.name ?? '???',
		[clothComponentId],
	);

	const propSelectorTitle = useMemo(
		() =>
			CLOTH_SECTIONS.find((e) => e.value === propComponentId)?.name ?? '???',
		[propComponentId],
	);

	const torsoSelectorTitle =
		CLOTH_SECTIONS.find((e) => e.value === ClothesComponentId.TORSO)?.name ??
		'???';
	const undershirtSelectorTitle =
		CLOTH_SECTIONS.find((e) => e.value === ClothesComponentId.UNDERSHITS)
			?.name ?? '???';

	const updateSelectors = (
		componentId: ClothesComponentId,
		drawableId: number,
		textureId: number,
	) => {
		setCurrentComponents((prevValue) => ({
			...prevValue,
			[componentId]: {
				drawable: drawableId,
				texture: textureId,
			} as IComponent,
		}));
	};

	const handleResetClothes = useCallback(
		(options: { isFullReset: boolean }) => {
			const resetComponents: Partial<Record<ClothesComponentId, IComponent>> =
				{};

			if (!options.isFullReset) {
				resetComponents[clothComponentId] = initialComponents[clothComponentId];
			} else {
				for (
					let i = ClothesComponentId.HEAD;
					i <= ClothesComponentId.TOPS;
					i++
				) {
					resetComponents[i] = initialComponents[i];
				}
			}

			setCurrentComponents((prevValue) => ({
				...prevValue,
				...resetComponents,
			}));

			if (!options.isFullReset) {
				return;
			}

			for (let i = ClothesComponentId.HEAD; i <= ClothesComponentId.TOPS; i++) {
				if (i === clothComponentId) {
					continue;
				}

				rage.clothesTester
					.applyComponents(
						i,
						resetComponents[i]!.drawable,
						resetComponents[i]!.texture,
						resetComponents[ClothesComponentId.TORSO]!.drawable,
						resetComponents[ClothesComponentId.TORSO]!.texture,
						resetComponents[ClothesComponentId.UNDERSHITS]!.drawable,
						resetComponents[ClothesComponentId.UNDERSHITS]!.texture,
						isHatHidden,
						areGlassesHidden,
						isBellyOpen,
					)
					.catch((err) => rage.notifications.showException(err));
			}
		},
		[
			initialComponents,
			currentComponents,
			clothComponentId,
			isHatHidden,
			areGlassesHidden,
			isBellyOpen,
		],
	);

	const handleResetProps = useCallback(
		(options: { isFullReset: boolean }) => {
			const resetComponents: Partial<Record<ClothesComponentId, IComponent>> =
				{};

			if (!options.isFullReset) {
				resetComponents[propComponentId] = initialComponents[propComponentId];
			} else {
				for (
					let i = ClothesComponentId.HATS;
					i <= ClothesComponentId.BRACELETS;
					i++
				) {
					resetComponents[i] = initialComponents[i];
				}
			}

			setCurrentComponents((prevValue) => ({
				...prevValue,
				...resetComponents,
			}));

			if (!options.isFullReset) {
				return;
			}

			for (
				let i = ClothesComponentId.HATS;
				i <= ClothesComponentId.BRACELETS;
				i++
			) {
				if (i === propComponentId) {
					continue;
				}

				rage.clothesTester
					.applyComponents(
						i,
						resetComponents[i]!.drawable,
						resetComponents[i]!.texture,
						currentComponents[ClothesComponentId.TORSO].drawable,
						currentComponents[ClothesComponentId.TORSO].texture,
						currentComponents[ClothesComponentId.UNDERSHITS].drawable,
						currentComponents[ClothesComponentId.UNDERSHITS].texture,
						isHatHidden,
						areGlassesHidden,
						isBellyOpen,
					)
					.catch((err) => rage.notifications.showException(err));
			}
		},
		[
			initialComponents,
			currentComponents,
			propComponentId,
			isHatHidden,
			areGlassesHidden,
			isBellyOpen,
		],
	);

	return (
		<Fragment>
			<StyledContainer $side={'left'}>
				<DropdownBlock
					title={'Слой одежды'}
					id={'comp-layer'}
					options={CLOTH_SECTIONS.filter((e) => e.isVisible && !e.isProp)}
					onChange={(o) =>
						handleDropdownChange(o.value as ClothesComponentId, {
							isProp: false,
						})
					}
				/>
				<SelectorBlock
					title={clothSelectorTitle}
					drawableId={currentComponents[clothComponentId].drawable}
					maxDrawableId={currentComponentsRanges[clothComponentId].drawable[1]}
					textureId={currentComponents[clothComponentId].texture}
					minTextureId={currentComponentsRanges[clothComponentId].texture[0]}
					maxTextureId={currentComponentsRanges[clothComponentId].texture[1]}
					onDrawableChange={(drawableId) =>
						updateSelectors(clothComponentId, drawableId, 0)
					}
					onTextureChange={(textureId) =>
						updateSelectors(
							clothComponentId,
							currentComponents[clothComponentId].drawable,
							textureId,
						)
					}
				/>
				{clothComponentId === ClothesComponentId.TOPS && (
					<SelectorBlock
						title={torsoSelectorTitle}
						drawableId={currentComponents[ClothesComponentId.TORSO].drawable}
						maxDrawableId={
							currentComponentsRanges[ClothesComponentId.TORSO].drawable[1]
						}
						textureId={currentComponents[ClothesComponentId.TORSO].texture}
						minTextureId={
							currentComponentsRanges[ClothesComponentId.TORSO].texture[0]
						}
						maxTextureId={
							currentComponentsRanges[ClothesComponentId.TORSO].texture[1]
						}
						onDrawableChange={(drawableId) =>
							updateSelectors(ClothesComponentId.TORSO, drawableId, 0)
						}
						onTextureChange={(textureId) =>
							updateSelectors(
								ClothesComponentId.TORSO,
								currentComponents[ClothesComponentId.TORSO].drawable,
								textureId,
							)
						}
					/>
				)}
				{clothComponentId === ClothesComponentId.TOPS && (
					<SelectorBlock
						title={undershirtSelectorTitle}
						drawableId={
							currentComponents[ClothesComponentId.UNDERSHITS].drawable
						}
						maxDrawableId={
							currentComponentsRanges[ClothesComponentId.UNDERSHITS].drawable[1]
						}
						textureId={currentComponents[ClothesComponentId.UNDERSHITS].texture}
						minTextureId={
							currentComponentsRanges[ClothesComponentId.UNDERSHITS].texture[0]
						}
						maxTextureId={
							currentComponentsRanges[ClothesComponentId.UNDERSHITS].texture[1]
						}
						onDrawableChange={(drawableId) =>
							updateSelectors(ClothesComponentId.UNDERSHITS, drawableId, 0)
						}
						onTextureChange={(textureId) =>
							updateSelectors(
								ClothesComponentId.UNDERSHITS,
								currentComponents[ClothesComponentId.UNDERSHITS].drawable,
								textureId,
							)
						}
					/>
				)}
				<ButtonActions
					title={'Действия'}
					onReset={() => handleResetClothes({ isFullReset: false })}
					onResetAll={() => handleResetClothes({ isFullReset: true })}
					onExit={() => rage.clothesTester.exit()}
				/>
			</StyledContainer>
			<StyledContainer $side={'right'}>
				<DropdownBlock
					title={'Слой пропов'}
					id={'prop-layer'}
					options={CLOTH_SECTIONS.filter((e) => e.isVisible && e.isProp)}
					onChange={(o) =>
						handleDropdownChange(o.value as ClothesComponentId, {
							isProp: true,
						})
					}
				/>
				<SelectorBlock
					title={propSelectorTitle}
					drawableId={currentComponents[propComponentId].drawable}
					maxDrawableId={currentComponentsRanges[propComponentId].drawable[1]}
					textureId={currentComponents[propComponentId].texture}
					minTextureId={currentComponentsRanges[propComponentId].texture[0]}
					maxTextureId={currentComponentsRanges[propComponentId].texture[1]}
					onDrawableChange={(drawableId) =>
						updateSelectors(propComponentId, drawableId, 0)
					}
					onTextureChange={(textureId) =>
						updateSelectors(
							propComponentId,
							currentComponents[propComponentId].drawable,
							textureId,
						)
					}
				/>
				<ToggleBlock title={'Скрывать шляпы'} onChange={setIsHatHidden} />
				<ToggleBlock title={'Скрывать очки'} onChange={setAreGlassesHidden} />
				<ToggleBlock title={'Открытый торс'} onChange={setIsBellyOpen} />
				<ButtonActions
					title={'Действия'}
					onReset={() => handleResetProps({ isFullReset: false })}
					onResetAll={() => handleResetProps({ isFullReset: true })}
				/>
			</StyledContainer>
		</Fragment>
	);
};

export { ClothesTester };
